import { useEffect, useState } from "react";
import { CodeFile } from "utils/interfaces";
import { useParams } from "react-router-dom";
import { getCollectionRequest } from "services/apiRequests";
import { PythonJSEnvironmentContext } from "components/utils/Contexts";
import { defaultPythonCode, defaultJavaScriptCode } from "config/default";
import CodeEditor from "./CodeEditor";
import CodeEditorHeader from "./CodeEditorHeader";

interface PythonJSEnvironmentProps {
  name: string;
  video: string;
  pdf: string;
}

// Parameters
const pythonJSHeaderOffset = 123;

const PythonJSEnvironment: React.FC<PythonJSEnvironmentProps> = ({
  name,
  video,
  pdf,
}) => {
  // States definition
  const [initialCode, setInitialCode] = useState<CodeFile[]>([]);
  const [currentCode, setCurrentCode] = useState<CodeFile[]>([]);
  const [activeItem, setActiveItem] = useState<"editor" | "assistant">(
    "editor"
  ); // to change between editor and assistant views
  const [videoExpanded, setVideoExpanded] = useState(false);
  const [showEditor, setShowEditor] = useState(false);

  // Get the url parameters
  const { moduleOrProject, id } = useParams();

  /**
   * Function used to get the initial code, or the user's
   * last saved code (if any) for this lesson
   * @returns
   */
  const getInitialCode = async (): Promise<CodeFile[]> => {
    // Define a code variable
    let code = [];

    // Get the code
    const response = await getCollectionRequest(
      `/api/code/python-js?type=${moduleOrProject}&id=${id}`
    );

    // Check if the response was successful
    if (response.successful) {
      code = response.content;
    }
    // Otherwise, define the default code for this
    // environment
    else {
      code = [
        {
          name: "example.py",
          content: defaultPythonCode,
        },
        {
          name: "example.js",
          content: defaultJavaScriptCode,
        },
      ];
    }

    // Check if the content is not void
    if (!(code && code.length > 0)) return [];

    // Set the initial and current code based on this
    setInitialCode(code);
    setCurrentCode(code);
    // And show editor
    setShowEditor(true);

    return code;
  };

  /**
   * Hook to get the initial code
   */
  useEffect(() => {
    getInitialCode();
  }, []);

  return (
    <PythonJSEnvironmentContext.Provider
      value={{
        pythonJSHeaderOffset,
        activeItem,
        setActiveItem,
      }}
    >
      <div className="flex-grow relative">
        <div className="h-screen overflow-hidden">
          <CodeEditorHeader
            name={name}
            video={video}
            videoExpanded={videoExpanded}
            setVideoExpanded={setVideoExpanded}
            hideVideo={activeItem === "assistant"}
            showAssistant={true}
            codeFiles={currentCode}
          />
          {showEditor && (
            <CodeEditor
              key={name}
              initialCode={initialCode}
              pdf={pdf}
              onFileChange={(files) => setCurrentCode(files)}
              toShrink={videoExpanded}
              hide={activeItem === "assistant"}
            />
          )}
        </div>
      </div>
    </PythonJSEnvironmentContext.Provider>
  );
};

export default PythonJSEnvironment;
